import {
  type DirectPaymentOrderDetail,
  type DirectPaymentOrdersListQuerySchema,
  type Paginated,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { AWAITING_RECEIPTS_FILTERS } from '@/lib/route.constant';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The filter pair is the route's; typing it against the contract here is what keeps it a real query. */
const FILTERS: z.input<typeof DirectPaymentOrdersListQuerySchema> = AWAITING_RECEIPTS_FILTERS;

/**
 * The W6 bridge for the PAYMENTS DESK: how much work is waiting there, read by
 * the shell (the sidebar badge) which may import no module. One row of the
 * same list `modules/Payments` paginates, under the same two filters; only
 * `meta.total` is kept — the Dashboard's rule, never a stats endpoint that
 * could drift from the list. Invalidated from the bus on
 * `billing.order.changed`, so a decision taken in the module moves the badge
 * without waiting for the next poll.
 */
export const paymentsDeskLookupApi = createApi({
  reducerPath: 'paymentsDeskLookupApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['PaymentsDesk'],
  endpoints: (builder) => ({
    awaitingReceiptsCount: builder.query<number, void>({
      query: () => ({
        url: '/admin/direct-payment-orders',
        method: 'GET',
        params: { page: 1, pageSize: 1, ...FILTERS },
      }),
      transformResponse: (page: Paginated<DirectPaymentOrderDetail>) => page.meta.total,
      providesTags: ['PaymentsDesk'],
    }),
  }),
});

export const { useAwaitingReceiptsCountQuery } = paymentsDeskLookupApi;
